import tokens from '@kluein/design-tokens';
import { Anchor, Box } from '@kluein/klue-ui';
import { NectarIcon } from '@kluein/nectar';
import { useCallback, useMemo } from 'react';

import { useAuth } from 'contexts/auth';
import { useCardViewer } from 'hooks/use-card-viewer';
import { useRedirect } from 'hooks/use-redirect';
import { CARD_HEADER_ALLOWED_TAGS } from 'klue-html/constants';
import sanitize from 'klue-html/sanitizer';
import { AnalyticsAction } from 'lib/analytics';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { useCardInteractionLog } from '../../hooks';
import { KlueCardActionMenu } from '../klue-card-action-menu';
import { KlueCardBattlecardIndicator } from '../klue-card-battlecard-indicator';

import type { KlueCardHeaderProps } from './KlueCardHeader.types';

function KlueCardHeader(props: KlueCardHeaderProps) {
  const {
    isCardViewerContent,
    variant = 'default',
    cardData,
    onTitleHover,
    hideMenus,
    hideBattlecardIndicator = true,
    onTitleClick,
    rank,
    profileId = props.cardData?.board?.profileId, // TODO Fix KlueCard type
  } = props;
  const { isNewCardEditorEnabled, isCurator } = useAuth();
  const { open: openCardViewer } = useCardViewer();
  const cardName = cardData?.data?.name;
  const isSearchVariation = variant === 'search';
  const { redirectToCardEditor } = useRedirect();

  const cardTitle =
    (isSearchVariation && cardData?.data?.nameWithHighlight) || cardName || '';

  const htmlTitle = useMemo(
    () => ({
      __html: sanitize(cardTitle, {
        ALLOWED_TAGS: CARD_HEADER_ALLOWED_TAGS,
      }),
    }),
    [cardTitle],
  );

  const logCardInteraction = useCardInteractionLog();
  const handleOpenCardViewer = useCallback(
    (options?: { scrollToSources?: boolean; rank?: number | null }) => {
      if (cardData?.id) {
        openCardViewer(cardData.id, options);
        logCardInteraction({
          cardId: cardData.id,
          action: AnalyticsAction.expand,
          label: 'title',
          rank,
        });
      }
    },
    [cardData?.id, openCardViewer, logCardInteraction, rank],
  );

  const handleEditCard = useCallback(
    (options?: { rank?: number | null }) => {
      if (cardData?.id && profileId) {
        logCardInteraction({
          cardId: cardData.id,
          action: AnalyticsAction.edit,
          label: 'title',
          rank,
        });
        redirectToCardEditor({
          profileId,
          cardId: cardData.id,
        });
      }
    },
    [cardData?.id, logCardInteraction, profileId, rank, redirectToCardEditor],
  );

  const handleTitleClick = useCallback(() => {
    if (onTitleClick) {
      return onTitleClick();
    }

    if (isCurator && isNewCardEditorEnabled) {
      return handleEditCard({ rank });
    }

    return handleOpenCardViewer({ rank });
  }, [
    onTitleClick,
    isCurator,
    isNewCardEditorEnabled,
    handleEditCard,
    handleOpenCardViewer,
    rank,
  ]);

  const HeaderAnchor = useMemo(() => {
    return (props: { onClick?: () => void; 'data-test-id'?: string }) => (
      <Anchor
        size="large"
        dangerouslySetInnerHTML={htmlTitle}
        onMouseOver={() => onTitleHover?.(true)}
        onMouseLeave={() => onTitleHover?.(false)}
        data-tracking-id={TRACKING_IDS.global.card.title}
        style={{
          display: 'inline',
        }}
        {...props}
      />
    );
  }, [htmlTitle, onTitleHover]);

  if (cardData?.id === undefined) {
    return null;
  }

  if (isCardViewerContent) {
    return cardTitle ? (
      <h1
        data-test-id={TEST_IDS.klueCard.header.cardViewer}
        className="card-static-html_title"
      >
        <div dangerouslySetInnerHTML={htmlTitle} />
      </h1>
    ) : null;
  }

  let headerContent = null;

  if (isSearchVariation) {
    headerContent = cardTitle ? (
      <h1
        style={{
          padding: tokens.spacing.large,
          paddingBottom: 0,
          whiteSpace: 'pre-wrap',
        }}
        data-test-id={TEST_IDS.klueCard.header.search}
        className="card-static-html_title"
      >
        <HeaderAnchor onClick={handleTitleClick} />
      </h1>
    ) : null;
  } else {
    headerContent = (
      <Box
        direction="row"
        justify={cardTitle ? 'between' : 'end'}
        align="center"
        height={{ min: 'fit-content' }}
        margin={{ horizontal: 'large' }}
        pad={{ vertical: 'small' }}
        border={{ side: 'bottom', color: tokens.color.neutral.lightgrey.main }}
        data-test-id={TEST_IDS.klueCard.header.default}
      >
        {cardTitle && (
          <Box fill justify="center">
            <h1 className="card-static-html_title">
              <HeaderAnchor
                onClick={handleTitleClick}
                data-test-id={TRACKING_IDS.global.card.title}
              />
            </h1>
          </Box>
        )}
        {cardData && (!hideBattlecardIndicator || !hideMenus) && (
          <>
            {!hideBattlecardIndicator && (
              <KlueCardBattlecardIndicator
                cardId={cardData.id}
                profileId={profileId}
              />
            )}
            {!hideMenus && (
              <KlueCardActionMenu
                cardId={cardData.id}
                sourcesCount={cardData?.sourcesCount}
                profileId={profileId}
                rank={rank}
              />
            )}
          </>
        )}
        {variant === 'print' && (
          <Box>
            <NectarIcon.KlueLogo />
          </Box>
        )}
      </Box>
    );
  }

  return headerContent;
}

export default KlueCardHeader;
